import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, createBrowserRouter, useLocation, useParams } from "react-router-dom";
import { AuthGate } from "./AuthGate";
import { AppShell } from "./AppShell";
import { APP_BASE_PATH } from "./basePath";
import { RouteFallback } from "./RouteFallback";
const QaeCollectionPage = lazy(() => import("../pages/QaeCollectionPage").then((module) => ({ default: module.QaeCollectionPage })));
const FailuresPage = lazy(() => import("../pages/FailuresPage").then((module) => ({ default: module.FailuresPage })));
const LogsPage = lazy(() => import("../pages/LogsPage").then((module) => ({ default: module.LogsPage })));
const NewRunPage = lazy(() => import("../pages/NewRunPage").then((module) => ({ default: module.NewRunPage })));
const NotFoundPage = lazy(() => import("../pages/NotFoundPage").then((module) => ({ default: module.NotFoundPage })));
const QueuePage = lazy(() => import("../pages/QueuePage").then((module) => ({ default: module.QueuePage })));
const QuestionBankPage = lazy(() => import("../pages/QuestionBankPage").then((module) => ({ default: module.QuestionBankPage })));
const ReportsPage = lazy(() => import("../pages/ReportsPage").then((module) => ({ default: module.ReportsPage })));
const ReportComparisonPage = lazy(() => import("../pages/ReportComparisonPage").then((module) => ({ default: module.ReportComparisonPage })));
const ResultsPage = lazy(() => import("../pages/ResultsPage").then((module) => ({ default: module.ResultsPage })));
const RunDetailPage = lazy(() => import("../pages/RunDetailPage").then((module) => ({ default: module.RunDetailPage })));
const SettingsPage = lazy(() => import("../pages/SettingsPage").then((module) => ({ default: module.SettingsPage })));
const SuitesPage = lazy(() => import("../pages/SuitesPage").then((module) => ({ default: module.SuitesPage })));
const OverviewPage = lazy(() => import("../pages/OverviewPage").then((module) => ({ default: module.OverviewPage })));
const ProjectDetailPage = lazy(() => import("../pages/ProjectDetailPage").then((module) => ({ default: module.ProjectDetailPage })));
const ProjectQuestionBankPage = lazy(() => import("../pages/ProjectQuestionBankPage").then((module) => ({ default: module.ProjectQuestionBankPage })));
const ProjectSuitesPage = lazy(() => import("../pages/ProjectSuitesPage").then((module) => ({ default: module.ProjectSuitesPage })));
const ProjectsPage = lazy(() => import("../pages/ProjectsPage").then((module) => ({ default: module.ProjectsPage })));
const EvaluationProfilesPage = lazy(() => import("../pages/EvaluationProfilesPage").then((module) => ({ default: module.EvaluationProfilesPage })));
const ProjectPromptsPage = lazy(() => import("../pages/ProjectPromptsPage").then((module) => ({ default: module.ProjectPromptsPage })));
const ProjectTargetsPage = lazy(() => import("../pages/ProjectTargetsPage").then((module) => ({ default: module.ProjectTargetsPage })));
const ProjectSettingsPage = lazy(() => import("../pages/ProjectSettingsPage").then((module) => ({ default: module.ProjectSettingsPage })));
const ProjectProductionSamplesPage = lazy(() => import("../pages/ProjectProductionSamplesPage").then((module) => ({ default: module.ProjectProductionSamplesPage })));
const ProjectProductionCandidatePage = lazy(() => import("../pages/ProjectProductionCandidatePage").then((module) => ({ default: module.ProjectProductionCandidatePage })));
const CrossEvaluationsPage = lazy(() => import("../pages/CrossEvaluationsPage").then((module) => ({ default: module.CrossEvaluationsPage })));
const CrossSourcesPage = lazy(() => import("../pages/CrossSourcesPage").then((module) => ({ default: module.CrossSourcesPage })));
const CrossEvaluationDetailPage = lazy(() => import("../pages/CrossEvaluationDetailPage").then((module) => ({ default: module.CrossEvaluationDetailPage })));
const CrossEvaluationReportPage = lazy(() => import("../pages/CrossEvaluationReportPage").then((module) => ({ default: module.CrossEvaluationReportPage })));

function lazyRoute(children: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

function ProjectPathRedirect({ to }: { to: string }) {
  const { projectId = "" } = useParams();
  const location = useLocation();
  const suffix = to.startsWith("?") ? to : to;
  if (suffix.startsWith("?")) {
    return <Navigate replace to={`/projects/${encodeURIComponent(projectId)}/tasks${suffix}`} />;
  }
  return <Navigate replace to={`/projects/${encodeURIComponent(projectId)}/${suffix.replace(/^\//, "")}${location.search}`} />;
}

function LegacyProjectDetailRedirect({ section, tail = "" }: { section: "tasks" | "settings/sync"; tail?: string }) {
  const { projectId = "", runId = "", candidateId = "" } = useParams();
  const location = useLocation();
  const detailId = runId || candidateId;
  const detailPath = candidateId
    ? `${tail}/${encodeURIComponent(candidateId)}`
    : `${detailId ? `/${encodeURIComponent(detailId)}` : ""}${tail}`;
  return <Navigate replace to={`/projects/${encodeURIComponent(projectId)}/${section}${detailPath}${location.search}`} />;
}

export function createAppRouter() {
  return createBrowserRouter([
  {
    element: <AuthGate><AppShell /></AuthGate>,
    children: [
      { index: true, element: <Navigate replace to="/overview" /> },
      { path: "overview", element: lazyRoute(<OverviewPage />) },
      { path: "projects", element: lazyRoute(<ProjectsPage />) },
      { path: "projects/:projectId", element: lazyRoute(<ProjectDetailPage />) },

      // Content: suites-first; question bank as secondary route
      { path: "projects/:projectId/content", element: lazyRoute(<ProjectSuitesPage />) },
      { path: "projects/:projectId/content/qae-collection", element: lazyRoute(<QaeCollectionPage />) },
      { path: "projects/:projectId/content/questions", element: lazyRoute(<ProjectQuestionBankPage />) },
      { path: "projects/:projectId/content/suites/:suiteId", element: lazyRoute(<SuitesPage />) },
      { path: "projects/:projectId/suites", element: <ProjectPathRedirect to="content" /> },
      { path: "projects/:projectId/questions", element: <ProjectPathRedirect to="content/questions" /> },
      // Evaluation tasks: creation, queue state and execution operations.
      { path: "projects/:projectId/tasks", element: lazyRoute(<QueuePage />) },
      { path: "projects/:projectId/tasks/logs", element: lazyRoute(<LogsPage />) },
      { path: "projects/:projectId/tasks/:runId", element: lazyRoute(<RunDetailPage />) },
      { path: "projects/:projectId/tasks/:runId/failures", element: lazyRoute(<FailuresPage />) },
      { path: "projects/:projectId/tasks/:runId/logs", element: lazyRoute(<LogsPage />) },
      { path: "projects/:projectId/queue", element: <ProjectPathRedirect to="tasks" /> },
      { path: "projects/:projectId/new-run", element: <ProjectPathRedirect to="?newRun=1" /> },

      // Evaluation results: raw per-question outputs, completeness and scores.
      { path: "projects/:projectId/results", element: lazyRoute(<ResultsPage />) },
      { path: "projects/:projectId/results/:runId", element: lazyRoute(<RunDetailPage />) },
      { path: "projects/:projectId/results/:runId/failures", element: lazyRoute(<FailuresPage />) },
      { path: "projects/:projectId/results/:runId/logs", element: lazyRoute(<LogsPage />) },

      // Legacy run routes keep old bookmarks valid while moving users to the new IA.
      { path: "projects/:projectId/runs", element: <ProjectPathRedirect to="tasks" /> },
      { path: "projects/:projectId/runs/logs", element: <LegacyProjectDetailRedirect section="tasks" tail="/logs" /> },
      { path: "projects/:projectId/runs/:runId", element: <LegacyProjectDetailRedirect section="tasks" /> },
      { path: "projects/:projectId/runs/:runId/failures", element: <LegacyProjectDetailRedirect section="tasks" tail="/failures" /> },
      { path: "projects/:projectId/runs/:runId/logs", element: <LegacyProjectDetailRedirect section="tasks" tail="/logs" /> },

      // Settings workshop
      { path: "projects/:projectId/settings", element: lazyRoute(<ProjectSettingsPage />) },
      { path: "projects/:projectId/settings/env", element: lazyRoute(<ProjectTargetsPage />) },
      { path: "projects/:projectId/settings/eval", element: lazyRoute(<EvaluationProfilesPage />) },
      { path: "projects/:projectId/settings/prompts", element: lazyRoute(<ProjectPromptsPage />) },
      { path: "projects/:projectId/settings/sync", element: lazyRoute(<ProjectProductionSamplesPage />) },
      { path: "projects/:projectId/settings/sync/candidates/:candidateId", element: lazyRoute(<ProjectProductionCandidatePage />) },
      { path: "projects/:projectId/targets", element: <ProjectPathRedirect to="settings/env" /> },
      { path: "projects/:projectId/evaluation-profiles", element: <ProjectPathRedirect to="settings/eval" /> },
      { path: "projects/:projectId/prompts", element: <ProjectPathRedirect to="settings/prompts" /> },

      // Legacy production routes now live under project settings > data sync.
      { path: "projects/:projectId/production-samples", element: <LegacyProjectDetailRedirect section="settings/sync" /> },
      { path: "projects/:projectId/production-samples/candidates/:candidateId", element: <LegacyProjectDetailRedirect section="settings/sync" tail="/candidates" /> },

      { path: "projects/:projectId/reports", element: lazyRoute(<ReportsPage />) },
      { path: "projects/:projectId/reports/compare", element: lazyRoute(<ReportComparisonPage />) },
      { path: "projects/:projectId/reports/:runId", element: lazyRoute(<RunDetailPage />) },
      { path: "projects/:projectId/reports/:runId/failures", element: lazyRoute(<FailuresPage />) },
      { path: "projects/:projectId/reports/:runId/logs", element: lazyRoute(<LogsPage />) },
      { path: "cross-evaluations", element: lazyRoute(<CrossEvaluationsPage />) },
      { path: "cross-evaluations/sources", element: lazyRoute(<CrossSourcesPage />) },
      { path: "cross-evaluations/:batchId", element: lazyRoute(<CrossEvaluationDetailPage />) },
      { path: "cross-evaluations/:batchId/report", element: lazyRoute(<CrossEvaluationReportPage />) },
      { path: "connections", element: <Navigate replace to="/projects" /> },
      { path: "evaluation-profiles", element: <Navigate replace to="/projects" /> },
      { path: "new-run", element: lazyRoute(<NewRunPage />) },
      { path: "suites", element: lazyRoute(<SuitesPage />) },
      { path: "suites/:suiteId", element: lazyRoute(<SuitesPage />) },
      { path: "question-bank", element: lazyRoute(<QuestionBankPage />) },
      { path: "queue", element: lazyRoute(<QueuePage />) },
      { path: "reports", element: lazyRoute(<ReportsPage />) },
      { path: "reports/compare", element: lazyRoute(<ReportComparisonPage />) },
      { path: "runs/:runId", element: lazyRoute(<RunDetailPage />) },
      { path: "runs/:runId/failures", element: lazyRoute(<FailuresPage />) },
      { path: "logs", element: lazyRoute(<LogsPage />) },
      { path: "logs/:runId", element: lazyRoute(<LogsPage />) },
      { path: "settings", element: <Navigate replace to="/settings/security" /> },
      { path: "settings/environment", element: <Navigate replace to="/projects" /> },
      { path: "settings/judge", element: <Navigate replace to="/projects" /> },
      { path: "settings/:section", element: lazyRoute(<SettingsPage />) },
      { path: "*", element: lazyRoute(<NotFoundPage />) },
    ],
  },
  ], { basename: APP_BASE_PATH || "/" });
}
